import { useEffect, useRef, useCallback } from 'react';

import lottie from 'lottie-web';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';

import { selectAnimation, LOTTIE_AVATAR_PATH } from '#client/components/utils/constants';

/**
 * VeloAvatar Component
 *
 * A component that displays a Lottie avatar animation.
 *
 * Same Lottie is replicated in assistant-ui and dashboard-ui.
 * This is a copy of the VeloAvatar component in assistant-ui.
 * It should be updated in both places when changes are made till its added to ZUI.
 *
 * @param {object} props - Component props
 * @param {string} props.state - The state of the avatar (default, loading, error)
 * @returns {import('react').ReactElement} The VeloAvatar component
 * @param {number} props.width - The width of the avatar
 * @param {number} props.height - The height of the avatar
 * @param {string} props.color - The color of the avatar (default is #FAFAFA)
 * @component
 */
export default function VeloAvatar({
  state = 'default',
  width = 32,
  height = 32,
  color = '#FAFAFA'
}) {
  const containerRef = useRef(null);
  const animRef = useRef(null);

  // helper to recolor any white fills in the SVG
  const changeColor = useCallback((newColor) => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;
    const whitePaths = svg.querySelectorAll('path[fill="rgb(255,255,255)"]');
    whitePaths.forEach((path) => path.setAttribute('fill', newColor));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (!containerRef.current) {
      return undefined;
    }

    const params = {
      container: containerRef.current,
      renderer: 'svg',
      loop: false, // Let playSegments handle looping
      prerender: false,
      autoplay: false, // Manual control
      autoloadSegments: false,
      path: LOTTIE_AVATAR_PATH // Use Cloudinary URL
    };

    animRef.current = lottie.loadAnimation(params);

    animRef.current.addEventListener('DOMLoaded', () => {
      // Use the actual animation segments for each state
      const segments = selectAnimation[state] || selectAnimation.default;

      // Play the specific loop segment and set up continuous looping
      animRef.current.playSegments(segments.loop, true);

      // Apply color change after a short delay to ensure SVG is rendered
      setTimeout(() => {
        changeColor(color);
      }, 1);

      // Add multiple event listeners to ensure continuous looping
      const handleLoopComplete = () => {
        animRef.current.playSegments(segments.loop, true);
      };

      const handleComplete = () => {
        animRef.current.playSegments(segments.loop, true);
      };

      const handleSegmentStart = () => {
        // Segment started
      };

      animRef.current.addEventListener('loopComplete', handleLoopComplete);
      animRef.current.addEventListener('complete', handleComplete);
      animRef.current.addEventListener('segmentStart', handleSegmentStart);

      // Store the handlers so we can remove them later
      animRef.current.loopHandler = handleLoopComplete;
      animRef.current.completeHandler = handleComplete;
      animRef.current.segmentStartHandler = handleSegmentStart;
    });

    animRef.current.addEventListener('data_ready', () => {
      // Data ready
    });

    animRef.current.addEventListener('data_failed', (error) => {
      console.error('❌ Data failed for state:', state, error);
    });

    animRef.current.addEventListener('loaded_images', () => {
      // Images loaded
    });

    return () => {
      if (animRef.current) {
        // Remove all event handlers if they exist
        if (animRef.current.loopHandler) {
          animRef.current.removeEventListener('loopComplete', animRef.current.loopHandler);
        }
        if (animRef.current.completeHandler) {
          animRef.current.removeEventListener('complete', animRef.current.completeHandler);
        }
        if (animRef.current.segmentStartHandler) {
          animRef.current.removeEventListener('segmentStart', animRef.current.segmentStartHandler);
        }
        animRef.current.destroy();
      }
    };
  }, [changeColor, state, color]);

  return <Box ref={containerRef} style={{ width, height }} />;
}

VeloAvatar.propTypes = {
  state: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  color: PropTypes.string
};
